<template>
<div>

    <b-loading :is-full-page="true" :active.sync="isLoading" :can-cancel="false" />

    <!-- AUDIO UNLOCK AUDIO MODAL -->
    <div v-if="!audioUnlocked" class="audio-unlock-overlay">
        <div class="audio-unlock-modal">
            <p class="audio-unlock-title">{{ texts.waitingProfessionalMsg }}</p>
            <a
                class="button is-info"
                @click="unlockAudio('ringtone.mp3')"
            >
                {{ texts.accept }}
            </a>
        </div>
    </div>

    <!-- WAITING -->
    <div v-if="state == 'waiting'">
        <h1 class="title">{{ texts.teleEvaluation }}</h1>
        <p class="header-local-name">
            {{ user }}
        </p>
        <p class="waiting-room-msg">{{ texts.teleEvaluationMsg }}</p>
        <br>
        <div class="waiting-timer">
            {{ timerDisplay }}
        </div>
        <b-carousel
            :interval=5000
            :has-drag=false
            :arrow=false
            :indicator=false
            :pause-hover=false
        >
            <b-carousel-item v-for="(advice, i) in config.advices" :key="i">
                <div class="advice">
                    <img class="advice-image" :src="`/static/advices/${advice}`" />
                </div>
            </b-carousel-item>
        </b-carousel>
        <div
            class="actions actions-center"
        >
            <a
                class="button is-warning is-fullwidth btn-action"
                @click="disconnect()"
            >
                {{ texts.disconnect }}
            </a>
        </div>
    </div>

    <!-- CALLING -->
    <div v-if="state == 'calling'">
        <p class="header-remote-name">{{ remoteUser }}</p>
        <h1 class="title calling">{{ texts.calling }}</h1>
        <div class="slide-to-answer">
            <div class="slide-track" ref="slideTrack" :class="{ 'slide-accepted': slideAccepted }">
                <span v-if="!slideAccepted" class="slide-label">{{ texts.slideToAnswer }}</span>
                <span v-else class="slide-label accepted-label">✓</span>
                <div
                    class="slide-thumb"
                    :class="{ 'slide-thumb-accepted': slideAccepted }"
                    :style="{
                        left: slideX + 'px',
                        transition: slideTracking ? 'none' : 'left 0.3s ease'
                    }"
                    @mousedown="!slideAccepted && slideStart($event)"
                    @touchstart.prevent="!slideAccepted && slideStart($event)"
                >
                    <b-icon
                        pack="fas"
                        icon="video"
                        :style="{ color: slideAccepted ? '#4cd964' : 'white', fontSize: '1.4em' }"
                    />
                </div>
            </div>
        </div>
        <p class="accept-call-msg">{{ texts.acceptCallMessage }}</p>
        <div class="actions actions-center actions-bottom">
            <button
                class="button is-danger actions-btn"
                @click="hangup(false)"
            >
                <b-icon
                    pack="fas"
                    icon="times">
                </b-icon>
            </button>
        </div>
    </div>

    <!-- INCALL -->
    <div v-if="state == 'incall'" class="actions">
        <p class="header-remote-name">{{ remoteUser }}</p>
        <button style="visibility:hidden" class="button actions-btn" />
        <button
            v-show="!isChatOpened"
            class="button is-danger actions-btn"
            @click="hangup(true)"
        >
            <b-icon
                pack="fas"
                icon="times">
            </b-icon>
        </button>
        <button v-if="!config.userSettings" style="visibility:hidden" class="button actions-btn" />
        <div
            v-if="config.userSettings"
            v-show="!isChatOpened"
            class="actions-btn fab"
            :class="{
                'fab-hover': isFabHover
            }"
            @click="isFabHover = !isFabHover"
        >
            <span
                class="fab-action-button videortc-icon-Settings"
            >
            </span>
            <ul
                class="fab-buttons fab-buttons-reset-left"
            >
                <li
                    v-if="config.userSettings.chat"
                    class="fab-buttons-item"
                >
                    <a
                        class="fab-buttons-link fab-blue"
                        @click="openChat()"
                    >
                        <b-icon
                            pack="fas"
                            icon="comment-alt">
                        </b-icon>
                    </a>
                </li>
                <li
                    v-if="config.userSettings.toggleAudio"
                    class="fab-buttons-item"
                >
                    <a
                        class="fab-buttons-link"
                        @click="toggleAudio()"
                    >
                        <b-icon
                            pack="fas"
                            :icon="!isAudioMuted ? 'microphone' : 'microphone-slash'"
                        >
                        </b-icon>
                    </a>
                </li>
                <li
                    v-if="config.userSettings.toggleVideo"
                    class="fab-buttons-item"
                >
                    <a
                        class="fab-buttons-link"
                        @click="toggleVideo()"
                    >
                        <b-icon
                            pack="fas"
                            :icon="!isVideoMuted ? 'video' : 'video-slash'"
                        >
                        </b-icon>
                    </a>
                </li>
                <li
                    v-if="config.userSettings.replaceVideo"
                    class="fab-buttons-item"
                >
                    <a
                        class="fab-buttons-link"
                        @click="replaceVideo()"
                    >
                        <b-icon
                            pack="fas"
                            icon="sync-alt"
                        >
                        </b-icon>
                    </a>
                </li>
            </ul>
        </div>
    </div>
    <div class="slow-link" v-if="slowLink">{{ texts.slowLink }}</div>
    <div v-show="state == 'incall'" id="videocall"></div>
    <div v-show="state == 'incall' && isChatOpened" class="chat">
        <div
            class="chat-panel"
            @click="hideChat()"
        ></div>
        <div class="chat-actions">
            <b-field class="chat-input">
                <p class="control">
                    <b-button
                        class="button is-danger"
                        size="is-medium"
                        @click="hideChat(true)"
                    >
                        <b-icon
                            pack="fas"
                            icon="times-circle"
                        />
                    </b-button>
                </p>
                <b-input
                    ref="chatMsg"
                    v-model="chatMsg"
                    v-on:keyup.native.enter="sendMsg()"
                    :placeholder="texts.typeMessage"
                    type="text"
                    size="is-medium"
                    expanded
                />
                <p class="control">
                    <b-button
                        class="button is-info"
                        size="is-medium"
                        @click="sendMsg()"
                    >
                        <b-icon
                            pack="fas"
                            icon="arrow-alt-circle-right"
                        />
                    </b-button>
                </p>
            </b-field>
        </div>
    </div>
</div>
</template>

<script>
import moment from 'moment';
import { Utils } from '../../mixins/Utils';
import HealthCareService from '../../services/HealthCareService';

export default {
    name: 'CallUser',
    mixins: [ Utils ],
    data () {
        return {
            isFabHover: false,
            config: {},
            texts: {},
            role: 'u',
            state: 'waiting', // 'waiting' || 'calling' || 'incall'
            session: null,
            sessionData: {},
            user: null,
            professionalAlias: null,
            userGw: null,
            remoteUser: null,
            remoteUserGw: null,
            patternProffesional: null,
            patternUser: null,
            videortc: {
                app: null,
                ucases: null,
                actions: null,
            },
            callingTone: null,
            isLoading: true,
            chatMsg: '',
            isChatOpened: false,
            isAudioMuted: false,
            isVideoMuted: false,
            remoteHasVideo: null,
            slowLink: null,
            slideX: 0,
            slideTracking: false,
            slideStartX: 0,
            slideAccepted: false,
            timerDisplay: '',
            timerInterval: null,
            audioUnlocked: false,
        }
    },
    mounted() {
        this.config = JSON.parse(JSON.stringify(this.$parent.config));
        this.texts = JSON.parse(JSON.stringify(this.$parent.texts));
        this.user = localStorage.getItem('username');
        this.session = localStorage.getItem('session');
        this.patternProffesional = /^([a-zA-Z0-9]+)\|(p)\|([^\|]+)$/;
        this.patternUser = /^([a-zA-Z0-9]+)\|(u)\|([^\|]+)\|(\d+)\|(\d+)\|([^\|]+)$/;
        HealthCareService.getSession(this.session)
            .then(res => {
                this.sessionData = res.data;
                this.startTimer();
                this.userGw = `${this.config.prefix}|${this.role}|${this.user}|${moment(this.sessionData.start).unix()}|${moment(this.sessionData.end).unix()}|${this.sessionData.professional}`;
                this.connect();
            })
            .catch(error => {
                const err = (error.response && error.response.data && error.response.data.error) ? error.response.data.error : 'Error';
                this.$buefy.toast.open({ message: err, type: 'is-danger', position: 'is-bottom' });
                setTimeout(() => {
                    this.$router.push('/');
                }, 1000);
            })
    },
    methods: {
        unlockAudio(audio) {
            this.initAndUnlockAudio(audio);
            this.audioUnlocked = true;
        },
        startTimer() {
            this.timerInterval = setInterval(() => {
                const start = moment(this.sessionData.start);
                const now = moment();
                const diff = now.diff(start, 'seconds');
                const abs = Math.abs(diff);
                const h = Math.floor(abs / 3600);
                const m = Math.floor((abs % 3600) / 60);
                const s = abs % 60;
                const mm = String(m).padStart(2, '0');
                const ss = String(s).padStart(2, '0');
                if (h > 0) this.timerDisplay = `${h}h ${mm}:${ss}`;
                else this.timerDisplay = `${mm}:${ss}`;
            }, 1000);
        },
        slideStart(e) {
            this.slideTracking = true;
            this.slideStartX = (e.touches ? e.touches[0].pageX : e.pageX) - this.slideX;
            window.addEventListener('mousemove', this.slideMove);
            window.addEventListener('mouseup', this.slideEnd);
            window.addEventListener('touchmove', this.slideMove);
            window.addEventListener('touchend', this.slideEnd);
        },
        slideMove(e) {
            if (!this.slideTracking) return;
            const track = this.$refs.slideTrack;
            const pageX = e.touches ? e.touches[0].pageX : e.pageX;
            const max = track.offsetWidth - 70;
            let x = pageX - this.slideStartX;
            if (x < 0) x = 0;
            if (x >= max) {
                x = max;
                this.slideX = x;
                this.slideEnd(null, true);
                return;
            }
            this.slideX = x;
        },
        slideEnd(e, accepted = false) {
            this.slideTracking = false;
            window.removeEventListener('mousemove', this.slideMove);
            window.removeEventListener('mouseup', this.slideEnd);
            window.removeEventListener('touchmove', this.slideMove);
            window.removeEventListener('touchend', this.slideEnd);
            if (accepted) {
                this.slideAccepted = true;
                this.accept();
            } else {
                this.slideX = 0;
            }
        },
        toggleAudio() {
            this.isAudioMuted = this.videortc.actions.toggleAudio();
        },
        toggleVideo() {
            this.isVideoMuted = this.videortc.actions.toggleVideo();
        },
        replaceVideo() {
            if (!this.config.replaceVideo) return;
            this.setLoading(true);
            this.videortc.actions.replaceVideo('next');
            setTimeout(() => {
                this.setLoading(false);
            }, 500);
        },
        setLoading(isLoading) {
            this.isLoading = isLoading;
        },
        connect() {
            this.videortc.app = new VideoRTC(server, iceServers, debugLevel, apiSecret);
            this.videortc.app.connect()
                .then(usecases => {
                    this.ucases = usecases;
                    this.startVideocall();
                })
                .catch(cause => {
                    this.setLoading(false);
                    this.$buefy.toast.open({ message: cause || 'Error connecting with the VideoRTC', type: 'is-danger', position: 'is-bottom' });
                });
        },
        startVideocall() {
            const onEvents = {
                onAccepted: () => {
                    this.vueInsomnia().off();
                    if (this.callingTone) this.stopSound(this.callingTone);
                    this.state = 'incall';
                    setTimeout(() => {
                        this.videortc.actions.setCall(true, true, this.config.quality);
                    }, 5000);
                },
                onCalling: () => {},
                onGotPeers: list => {},
                onHangUp: (user, reason) => {
                    this.slowLink = null;
                    if (this.callingTone) this.stopSound(this.callingTone);
                    this.state = 'waiting';
                },
                onIncomingCall: user => {
                    this.unlockAudio('ringtone.mp3'); // in case the call starts before the user unlocks the audio
                    if (window.navigator.vibrate) window.navigator.vibrate(200);
                    this.remoteUserGw = user;
                    const matches = this.remoteUserGw.match(this.patternProffesional) || this.remoteUserGw.match(this.patternUser);
                    this.remoteUser = `${matches[3]}`;
                    if (this.remoteUser === this.sessionData.professional && this.sessionData.professionalAlias) this.remoteUser = this.sessionData.professionalAlias;
                    this.slideX = 0;
                    this.slideAccepted = false;
                    this.slideTracking = false;
                    this.state = 'calling';
                    this.callingTone = this.playSound('ringtone.mp3', true);
                },
                onRegistered: (user, isRegistered) => {
                    this.vueInsomnia().on();
                    if (!isRegistered) {
                        this.$buefy.toast.open({ message: this.texts.connectingError, type: 'is-danger', position: 'is-bottom' });
                        this.disconnect();
                    }
                },
                onSetCall: () => {

                },
                onDataReceived: (type, data, filename) => {
                    if(type === 'application/x-chat') {
                        this.$buefy.notification.open({
                            message: data,
                            type: 'is-success',
                            closable: false,
                            position: 'is-top-right',
                            duration: 30000,
                            queue: false
                        })
                    }
                },
                onRemoteStreamReady: (isReady, stream) => {
                    if (isReady) this.remoteHasVideo = true;
                    if (this.remoteHasVideo) {
                        this.slowLink = !isReady;
                    }
                },
            };
            const domElements = {
                videos: document.getElementById('videocall'),
            };
            const options = {
                stream: {
                    resolution: this.config.resolution,
                },
                dataChannel: {
                    dataEnabled: this.config.chat,
                },
                display: {
                    namePattern: null,
                },
            };
            if (this.config.account) options.account = this.config.account;
            if (this.config.recordingPrefix) options.recordingPrefix = this.config.recordingPrefix;
            this.ucases.videoCall(onEvents, domElements, options)
                .then(acts => {
                    this.videortc.actions = acts;
                    this.register();
                    this.setLoading(false);
                })
                .catch(cause => {
                    this.setLoading(false);
                    this.$buefy.toast.open({ message: cause || 'Error', type: 'is-danger', position: 'is-bottom' });
                });
        },
        disconnect() {
            if (this.timerInterval) clearInterval(this.timerInterval);
            this.videortc.app.disconnect();
            this.$router.push(`/join/${this.session}/accept`);
        },
        register() {
            this.videortc.actions.register(this.userGw);
        },
        accept() {
            this.videortc.actions.acceptCall();
        },
        hangup(confirmation) {
            if (!confirmation) {
                this.videortc.actions.hangUp();
                this.state = 'waiting';
                return;
            }
            this.$buefy.dialog.confirm({
                message: this.texts.endCall,
                cancelText: this.texts.no,
                confirmText: this.texts.yes,
                type: 'is-success',
                onConfirm: () => {
                    this.videortc.actions.hangUp();
                    this.disconnect();
                },
            });
        },
        openChat() {
            this.isChatOpened = true;
            this.$refs.chatMsg.focus();
        },
        hideChat(conf) {
            if (conf || this.chatMsg === '') {
                this.isChatOpened = false;
                this.chatMsg = '';
            }
        },
        sendMsg() {
            this.videortc.actions.sendData(
                'application/x-chat',
                this.chatMsg,
                ok => {
                    this.chatMsg = '';
                    this.hideChat();
                },
                ko => {});
        },
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.audio-unlock-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
}

.audio-unlock-modal {
    background: white;
    border-radius: 6px;
    padding: 2rem;
    max-width: 400px;
    width: 90%;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.audio-unlock-title {
    font-size: 1.1rem;
    color: #363636;
}
.waiting-room-msg {
    white-space: pre-wrap;
    max-width: 85%;
    margin: auto;
    color: #363636;
    opacity: 0.6;
}
.waiting-timer {
    font-size: 2em;
    font-weight: bold;
    text-align: center;
    margin: 0;
    color: #4a4a4a;
    font-variant-numeric: tabular-nums;
    letter-spacing: 2px;
}
.slide-to-answer {
    margin-top: 15vh;
    display: flex;
    justify-content: center;
    @media (max-height: 450px) {
        margin-top: 20px;
    }
}
.slide-track {
    position: relative;
    width: 260px;
    height: 72px;
    background: #929292;
    border-radius: 50px;
    border: 1.5px solid rgba(150, 150, 150, 0.3);
    display: flex;
    align-items: center;
    overflow: hidden;
}
.slide-thumb {
    position: absolute;
    left: 2px;
    width: 68px;
    height: 68px;
    background: #4cd964;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    touch-action: none;
    z-index: 2;
    &:active { cursor: grabbing; }
}
.slide-label {
    position: absolute;
    width: 100%;
    text-align: center;
    font-size: 15px;
    pointer-events: none;
    user-select: none;
    padding-left: 50px;
    background: linear-gradient(
        to right,
        #363636 0%,
        #363636 40%,
        #ffffff 50%,
        #363636 60%,
        #363636 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: shine 3s infinite linear;
}
@keyframes shine {
    0% {
        background-position: -310px center;
    }
    100% {
        background-position: 50px center;
    }
}
.slide-track.slide-accepted {
    background: #4cd964;
    transition: background 0.3s ease;
}
.slide-thumb.slide-thumb-accepted {
    animation: pulse-accept 0.6s ease infinite;
    background: #ffffff;
    :deep(.icon) { color: #4cd964 !important; }
}
.accepted-label {
    padding-left: 0 !important;
    font-size: 22px;
    font-weight: bold;
    color: white;
    -webkit-text-fill-color: white;
    animation: none;
    background: none;
}
@keyframes pulse-accept {
    0%   { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0.6); }
    50%  { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(255,255,255,0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0); }
}

.videortc-icon-Settings {
    font-size: 58px;
    padding-top: 2px !important;
    padding-left: 1px;
}
.fab {
    cursor: pointer;
}
.fab-hover .fab-buttons {
    opacity: 1;
    visibility: visible;
}
.fab-hover .fab-buttons-link {
    transform: scaleY(1) scaleX(1) translateY(-16px) translateX(0px);
}
/*.fab-action-button:hover + .fab-buttons .fab-buttons-link:before {
    visibility: visible;
    opacity: 1;
    transform: scale(1);
    transform-origin: right center 0;
    transition-delay: 0.3s;
}*/
.fab-action-button {
    padding-top: 18px;
    position: absolute;
    bottom: 0;
    display: block;
    width: 60px;
    height: 60px;
    background-color: rgb(212, 212, 212);
    color: #363636;
    border-radius: 50%;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -2px rgba(0, 0, 0, 0.2);
}
.fab-buttons {
    position: absolute;
    left: 0px;
    right: 0px;
    bottom: 45px;
    width: 100px;
    list-style: none;
    margin: 0;
    padding: 0;
    opacity: 0;
    visibility: hidden;
    transition: 0.2s;
}
.fab-buttons-reset-left {
    left: initial;
}
.fab-buttons-item {
    display: block;
    text-align: center;
    margin: 12px 0;
}
.fab-buttons-link {
    padding-top: 11px;
    color: #363636;
    display: inline-block;
    width: 48px;
    height: 48px;
    text-decoration: none;
    background-color: #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -2px rgba(0, 0, 0, 0.2);
    transform: scaleY(0.5) scaleX(0.5) translateY(0px) translateX(0px);
    -moz-transition: .3s;
    -webkit-transition: .3s;
    -o-transition: .3s;
    transition: .3s;
}
.fab-red {
    background: #ff3860;
    color: #fff;
}
.fab-blue {
    background: #167df0 !important;
    color: #fff;
}
.header-remote-name {
    position: fixed;
    z-index: 2;
    top: 15px;
    left: 50%;
    transform: translate(-50%);
    background: rgba(250,250,250,0.5);
    padding: 3px 5px;
    border-radius: 3px;
}
.accept-call-msg {
    margin: 45px auto;
    max-width: 90%;
}
.calling {
    margin-top: 20vh;
    @media (max-height: 450px) {
        margin-top: 10px;
        margin-bottom: 0px;
    }
}
.calling-btn {
    top: 22px;
    z-index: 3;
    height: 140px;
    width: 140px;
    border-radius: 70px;
    font-size: 2.2em;
    animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) infinite;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
    @media (max-height: 450px) {
        height: 100px;
        width: 100px;
        top: 55px;
    }
}
@keyframes shake {
    10%, 90% {
        transform: translate3d(-1px, 0, 0);
    }
    20%, 80% {
        transform: translate3d(2px, 0, 0);
    }
    30%, 50%, 70% {
        transform: translate3d(-4px, 0, 0);
    }
    40%, 60% {
        transform: translate3d(4px, 0, 0);
    }
}
.header-local-name {
    display: inline-block;
    font-size: 1em;
    background:rgba(230,230,230,0.5);
    padding: 3px 5px;
    border-radius: 5px;
    margin-bottom: 15px;
}
.actions {
    position: absolute;
    bottom: 20px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0px 20px;
    z-index: 2;
    .actions-btn {
        border-radius: 30px;
        height: 60px;
        width: 60px;
    }
}
.actions-center {
    justify-content: center;
    z-index: 2;
}
.actions-bottom {
    position: absolute;
    margin-bottom: 0;
}
.btn-action {
    height: 60px;
    font-size: 1.3em;
    bottom: 20px;
    width: auto;
    top: 0px;
    width: 100%;
    max-width: 500px;
}
.advice {
    margin: 20px;
    .advice-image {
        max-height: 35vh;
    }
}

.chat {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 400px;
    max-width: 100%;
    height: 100vh;
    z-index: 2;
    .chat-panel {
        height: calc(100% - 45px);
        width: 100%;
        position: fixed;
        left: 0;
    }
    .chat-actions {
        height: 45px;
        width: 100%;
        bottom: 0;
        position: absolute;
    }
}
.tag {
    height: auto;
    padding: 0px 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/user/Call.vue